import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ShieldAlert, ChevronRight, AlertCircle } from 'lucide-react';
import { useAuth, formatApiError } from '@/lib/auth';
import api from '@/lib/api';
import AdinkraPattern from '@/components/layout/AdinkraPattern';

export default function ChangePassword() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const isForced = !!user?.must_change_password;
  const [show, setShow] = useState(false);
  const [current, setCurrent] = useState('');
  const [next1, setNext1] = useState('');
  const [next2, setNext2] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setError('');
    if (next1 !== next2) { setError('New passwords do not match.'); return; }
    if (next1.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setBusy(true);
    try {
      await api.post('/auth/change-password', {
        current_password: isForced ? '' : current,
        new_password: next1,
      });
      await refresh();
      navigate(user?.role === 'super_admin' ? '/control-panel' : `/dashboard/${(user?.modules || [])[0] || 'financials'}`, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 relative overflow-hidden p-6">
      <div className="absolute inset-0 opacity-[0.04]"><AdinkraPattern color="#0F2A5C" /></div>
      <div className="relative w-full max-w-md bg-white border border-neutral-200 rounded-md p-8 shadow-sm" data-testid="change-password-card">
        <div className="flex items-center gap-3 mb-2">
          {isForced ? (
            <ShieldAlert className="w-6 h-6 text-amber" />
          ) : null}
          <div className="label-tag text-amber-600">{isForced ? 'Action required' : 'Settings'}</div>
        </div>
        <h1 className="font-display font-bold text-2xl text-navy mb-1">
          {isForced ? 'Set a new password' : 'Change password'}
        </h1>
        <p className="text-sm text-neutral-600 mb-6">
          {isForced
            ? 'Your password was reset by an administrator. Choose a new one before continuing.'
            : 'Enter your current password and choose a new one.'}
        </p>

        <form onSubmit={submit} className="space-y-4">
          {!isForced && (
            <Field label="Current password">
              <div className="relative">
                <input type={show ? 'text' : 'password'} value={current} onChange={(e) => setCurrent(e.target.value)} required className="inp pr-10" data-testid="cp-current" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </Field>
          )}
          <Field label="New password">
            <input type="password" value={next1} onChange={(e) => setNext1(e.target.value)} required minLength={8} className="inp" data-testid="cp-new1" />
          </Field>
          <Field label="Confirm new password">
            <input type="password" value={next2} onChange={(e) => setNext2(e.target.value)} required minLength={8} className="inp" data-testid="cp-new2" />
          </Field>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-rose-50 border border-rose-200 rounded-md text-sm text-rose-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /><span>{error}</span>
            </div>
          )}

          <button type="submit" disabled={busy} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="cp-submit">
            {busy ? 'Saving…' : (<>Save and continue <ChevronRight className="w-4 h-4" /></>)}
          </button>
          {isForced && (
            <button type="button" onClick={async () => { await logout(); navigate('/login'); }} className="w-full text-xs text-neutral-500 hover:text-rose-600">
              Sign out instead
            </button>
          )}
        </form>
      </div>
      <style>{`.inp{width:100%;padding:0.6rem 0.75rem;border:1px solid #E5E7EB;border-radius:6px;background:white;outline:none;transition:border .15s}.inp:focus{border-color:#0F2A5C;box-shadow:0 0 0 2px rgba(15,42,92,0.1)}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-neutral-600 font-semibold mb-1.5">{label}</span>
      {children}
    </label>
  );
}
